
import { useState, useEffect } from 'react';
import { X, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import BookingResultModal from './BookingResultModal';
import { checkBookingConflict, getBookedTimeSlots } from '@/utils/bookingValidation';
import { carMakes, carModels, years, carLogos } from "@/utils/carData";

interface BookingModalProps {
  shopName: string;
  shopCity?: string;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  onBookingSubmit?: () => Promise<void>;
  oilBrand?: string;
  oilName?: string;
  viscosity?: string;
}

const bookingSchema = z.object({
  name: z.string().min(2, { message: 'الاسم يجب أن يكون على الأقل حرفين' }),
  email: z.string().email({ message: 'يرجى إدخال بريد إلكتروني صحيح' }),
  phone: z.string().min(10, { message: 'يرجى إدخال رقم هاتف صحيح' }),
  date: z.string().min(1, { message: 'يرجى اختيار التاريخ' }),
  time: z.string().min(1, { message: 'يرجى اختيار الوقت' }),
  vehicleMake: z.string().min(1, { message: 'يرجى اختيار ماركة المركبة' }),
  vehicleModel: z.string().min(1, { message: 'يرجى اختيار موديل المركبة' }),
  vehicleYear: z.string().min(1, { message: 'يرجى اختيار سنة المركبة' }),
  notes: z.string().optional(),
});

type BookingFormValues = z.infer<typeof bookingSchema>;

const N8N_WEBHOOK_URL = 'https://autocarepalestine.app.n8n.cloud/webhook-test/BookingMassege';

const BookingModal = ({ 
  shopName, 
  shopCity, 
  isOpen, 
  onClose, 
  isLoading = false, 
  onBookingSubmit, 
  oilBrand = "", 
  oilName = "", 
  viscosity = "" 
}: BookingModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [bookedTimeSlots, setBookedTimeSlots] = useState<string[]>([]);
  const isMobile = useIsMobile();

  const timeSlots = ['09:00 AM', '10:00 AM', '11:00 AM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'];

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      date: '',
      time: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
      notes: '',
    },
  });

  const watchedDate = form.watch('date');
  const watchedVehicleMake = form.watch('vehicleMake');

  // Fetch booked time slots when shop and date change
  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (shopName && watchedDate) {
        const bookedSlots = await getBookedTimeSlots(shopName, watchedDate);
        setBookedTimeSlots(bookedSlots);
      } else {
        setBookedTimeSlots([]);
      }
    };

    fetchBookedSlots();
  }, [shopName, watchedDate]);

  const sendWebhookNotification = async (bookingData: any) => {
    try {
      console.log('Sending webhook notification to n8n...');
      await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          ...bookingData,
          timestamp: new Date().toISOString(),
          source: 'booking_modal'
        }),
      });
      console.log('Webhook notification sent successfully');
    } catch (error) {
      console.error('Failed to send webhook notification:', error);
    }
  };

  const onSubmit = async (data: BookingFormValues) => {
    setIsSubmitting(true);

    try {
      // Check for booking conflicts
      const hasConflict = await checkBookingConflict(shopName, data.date, data.time);
      
      if (hasConflict) {
        toast.error("هذا الموعد محجوز بالفعل. يرجى اختيار وقت آخر.");
        setIsSubmitting(false);
        return;
      }

      // Create properly typed booking data object
      const bookingData = {
        "Full Name": data.name || '',
        "Email": data.email || '',
        "Phone Number": data.phone ? Number(data.phone) : null,
        "Shop Name": shopName || '',
        "City": shopCity || '',
        "Date": data.date || null,
        "Time": data.time || '',
        "Vehicle Make": data.vehicleMake || '',
        "Vehicle Model": data.vehicleModel || '',
        "Year": data.vehicleYear ? Number(data.vehicleYear) : null,
        "Oil Brand": oilBrand || '',
        "Oil Viscosity": viscosity || '',
        "Payment Method": 'cash',
        "Payment Status": 'pending',
        "Notes": data.notes || null,
        "amount": 0
      };

      console.log('Submitting booking data:', JSON.stringify(bookingData, null, 2));

      // Save to Supabase "New Bookings" table
      const { data: result, error } = await supabase
        .from('New Bookings')
        .insert(bookingData)
        .select();

      if (error) {
        console.error('Supabase booking error:', error);
        setBookingSuccess(false);
        setResultMessage('فشل في الحجز. يرجى المحاولة مرة أخرى.');
        setShowResultModal(true);
        return;
      }

      console.log('Booking saved successfully:', result);

      // Send webhook notification to n8n
      await sendWebhookNotification(bookingData);

      // Show success modal
      setBookingSuccess(true);
      setResultMessage('تم حجز موعدك بنجاح! سيتم التواصل معك قريباً لتأكيد الموعد.');
      setShowResultModal(true);

      // Reset form
      form.reset();
      
    } catch (error) {
      console.error('Booking error:', error);
      setBookingSuccess(false);
      setResultMessage('حدث خطأ. يرجى المحاولة مرة أخرى.');
      setShowResultModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResultModalClose = () => {
    setShowResultModal(false);
    if (bookingSuccess) {
      onClose(); // Close the booking modal too if successful
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
        <div className="relative w-full max-w-lg mx-4 bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b bg-white">
            <h3 className="text-lg font-semibold text-autocare-blue truncate pr-2">
              احجز موعد في {shopName}
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4 sm:p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>الاسم الكامل</FormLabel>
                        <FormControl>
                          <Input placeholder="أحمد محمد" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>البريد الإلكتروني</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="ahmed@example.com" 
                            type="email" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>رقم الهاتف</FormLabel>
                      <FormControl>
                        <Input placeholder="059 123 4567" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="vehicleMake"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ماركة المركبة</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={(value) => {
                            field.onChange(value);
                            form.setValue('vehicleModel', '');
                          }}>
                            <SelectTrigger>
                              <SelectValue placeholder="اختر الماركة">
                                {field.value ? (
                                  <div className="flex items-center">
                                    {carLogos[field.value] && (
                                      <img 
                                        src={carLogos[field.value]} 
                                        alt={`${field.value} logo`}
                                        className="h-5 w-5 mr-2 object-contain"
                                      />
                                    )}
                                    <span>{field.value}</span>
                                  </div>
                                ) : (
                                  "اختر الماركة"
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {carMakes.map(make => (
                                <SelectItem key={make} value={make}>
                                  <div className="flex items-center">
                                    {carLogos[make] && (
                                      <img 
                                        src={carLogos[make]} 
                                        alt={`${make} logo`}
                                        className="h-5 w-5 mr-2 object-contain"
                                      />
                                    )}
                                    <span>{make}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="vehicleModel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>موديل المركبة</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange} disabled={!watchedVehicleMake}>
                            <SelectTrigger>
                              <SelectValue placeholder="اختر الموديل" />
                            </SelectTrigger>
                            <SelectContent>
                              {watchedVehicleMake && carModels[watchedVehicleMake] ? carModels[watchedVehicleMake].map(model => (
                                <SelectItem key={model} value={model}>{model}</SelectItem>
                              )) : null}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="vehicleYear"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>السنة</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="اختر السنة" />
                            </SelectTrigger>
                            <SelectContent>
                              {years.map(year => (
                                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>التاريخ</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input 
                              type="date" 
                              {...field} 
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                              <Calendar className="h-4 w-4 text-gray-500" />
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>الوقت</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger>
                              <SelectValue placeholder="اختر الوقت" />
                            </SelectTrigger>
                            <SelectContent>
                              {timeSlots.map(slot => {
                                const isBooked = bookedTimeSlots.includes(slot);
                                return (
                                  <SelectItem 
                                    key={slot} 
                                    value={slot} 
                                    disabled={isBooked}
                                    className={isBooked ? 
                                      "opacity-50 cursor-not-allowed bg-red-50 text-red-400 hover:bg-red-50 hover:text-red-400" : 
                                      "cursor-pointer hover:bg-gray-100"
                                    }
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <span className={isBooked ? "line-through" : ""}>{slot}</span>
                                      {isBooked && (
                                        <span className="text-red-500 text-xs ml-2 font-semibold bg-red-100 px-2 py-1 rounded">
                                          محجوز
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                        {bookedTimeSlots.length > 0 && (
                          <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-sm">
                            <p className="text-orange-800 font-medium text-xs">
                              ⚠️ بعض المواعيد محجوزة لهذا التاريخ
                            </p>
                            <p className="text-orange-600 text-xs mt-1">
                              المواعيد المحجوزة معطلة ومميزة
                            </p>
                          </div>
                        )}
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ملاحظات إضافية (اختياري)</FormLabel>
                      <FormControl>
                        <textarea 
                          className="flex h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                          placeholder="أي طلبات خاصة أو معلومات إضافية..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-3 pt-3">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={onClose}
                    className="text-sm"
                    size={isMobile ? "sm" : "default"}
                  >
                    إلغاء
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-autocare-blue hover:bg-autocare-blue-light text-sm"
                    disabled={isSubmitting}
                    size={isMobile ? "sm" : "default"}
                  >
                    {isSubmitting ? 'جاري الحجز...' : 'احجز الموعد'}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>

      {/* Booking Result Modal */}
      <BookingResultModal 
        isOpen={showResultModal}
        isSuccess={bookingSuccess}
        message={resultMessage}
        onClose={handleResultModalClose}
      />
    </>
  );
};

export default BookingModal;
